import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { X, Loader2 } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

interface Pipeline {
  id: string;
  name: string;
}

interface Stage {
  id: string;
  name: string;
  pipelineId?: string;
  pipeline_id?: string;
}

interface ProfileModalProps {
  profile?: any;
  onSave: (data: any) => void;
  onClose: () => void;
}

export default function ProfileModal({ profile, onSave, onClose }: ProfileModalProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [formData, setFormData] = useState({
    source: 'facebook',
    formId: '',
    campaignId: '',
    name: '',
    isActive: true,
    targetStageId: '',
    targetPipelineId: '',
  });

  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [stages, setStages] = useState<Stage[]>([]);
  const [crmLoading, setCrmLoading] = useState(false);

  useEffect(() => {
    setCrmLoading(true);
    fetch('/api/crm/board', { credentials: 'include' })
      .then(async (r) => {
        if (!r.ok) return null;
        return r.json();
      })
      .then((data) => {
        if (data) {
          setPipelines(data.pipelines || []);
          setStages(data.stages || []);
        }
      })
      .catch(() => {})
      .finally(() => setCrmLoading(false));
  }, []);

  useEffect(() => {
    if (profile) {
      const pipelineId = profile.targetPipelineId || '';
      let stageId = profile.targetStageId || '';

      if (!pipelineId && stageId && stages.length > 0) {
        const stage = stages.find((s) => s.id === stageId);
        if (stage) {
      setFormData({
        source: 'facebook',
        formId: profile.formId || '',
        campaignId: profile.campaignId || '',
        name: profile.name || '',
        isActive: profile.isActive !== false,
        targetStageId: stageId,
        targetPipelineId: (stage.pipelineId ?? stage.pipeline_id) || '',
      });
          return;
        }
      }

      setFormData({
        source: 'facebook',
        formId: profile.formId || '',
        campaignId: profile.campaignId || '',
        name: profile.name || '',
        isActive: profile.isActive !== false,
        targetStageId: stageId,
        targetPipelineId: pipelineId,
      });
    }
  }, [profile, stages]);

  const filteredStages = formData.targetPipelineId
    ? stages.filter(
        (s) =>
          (s.pipelineId ?? s.pipeline_id) === formData.targetPipelineId
      )
    : stages;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.formId) {
      alert(t('profileModal.fillRequired'));
      return;
    }
    onSave({
      ...formData,
      source: 'facebook',
      targetStageId: formData.targetStageId.trim() || null,
      targetPipelineId: formData.targetPipelineId.trim() || null,
    });
  };

  // safe-area: top inset
  return (
    <>
      {/* safe-area: top inset */}
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 pt-[env(safe-area-inset-top)] px-[env(safe-area-inset-right)] pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)]">
      <div className={cn("bg-background border rounded-lg w-full max-h-[90vh] overflow-y-auto", isMobile ? "max-w-full" : "max-w-md")}>
        <div className={cn("flex items-center justify-between border-b sticky top-0 bg-background", isMobile ? "p-4" : "p-6")}>
          <h2 className="text-lg font-semibold">
            {profile ? t('profileModal.editTitle') : t('profileModal.newTitle')}
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-muted rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={cn("space-y-4", isMobile ? "p-4 space-y-3" : "p-6 space-y-4")}>
          <div>
            <label className="block text-sm font-medium mb-1">{t('profileModal.profileName')} *</label>
            <Input
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder={t('mappingsExtra.profileNamePlaceholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">{t('profileModal.sourceFacebookOnly')}</label>
            <p className="text-sm text-muted-foreground rounded-md border bg-muted/30 px-3 py-2">
              {t('profileModal.facebookLeadAds')}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">{t('profileModal.formId')} *</label>
            <Input
              value={formData.formId}
              onChange={(e) =>
                setFormData({ ...formData, formId: e.target.value })
              }
              placeholder={t('mappingsExtra.profileIdPlaceholder')}
            />
            <p className="text-xs text-foreground/60 mt-1">
              {t('profileModal.formIdFacebookOnly')}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">{t('profileModal.targetPipeline')}</label>
            {crmLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('common.loading')}
              </div>
            ) : (
              <Select
                value={formData.targetPipelineId || '__none__'}
                onValueChange={(v) =>
                  setFormData({
                    ...formData,
                    targetPipelineId: v === '__none__' ? '' : v,
                    targetStageId: '',
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('profileModal.selectPipeline')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">{t('profileModal.allPipelines')}</SelectItem>
                  {pipelines.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">{t('profileModal.targetStage')}</label>
            {crmLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('common.loading')}
              </div>
            ) : (
              <Select
                value={formData.targetStageId || '__none__'}
                onValueChange={(v) =>
                  setFormData({ ...formData, targetStageId: v === '__none__' ? '' : v })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('profileModal.selectStage')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">{t('profileModal.defaultStage')}</SelectItem>
                  {filteredStages.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <p className="text-xs text-foreground/60 mt-1">
              {t('profileModal.targetStageHint')}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">{t('profileModal.campaignId')}</label>
            <Input
              value={formData.campaignId}
              onChange={(e) =>
                setFormData({ ...formData, campaignId: e.target.value })
              }
              placeholder={t('mappingsExtra.profileIdPlaceholder')}
            />
          </div>

          <div className="flex items-center gap-2 pt-2">
            <Checkbox
              id="isActive"
              checked={formData.isActive}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, isActive: checked as boolean })
              }
            />
            <label htmlFor="isActive" className="text-sm cursor-pointer">
              {t('profileModal.activeProfile')}
            </label>
          </div>

          <div className={cn("flex gap-2 pt-4", isMobile && "flex-col")}>
            <Button
              type="submit"
              className={cn("flex-1 bg-primary text-primary-foreground", isMobile && "w-full")}
            >
              {profile ? t("common.update") : t("common.create")}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className={cn("flex-1", isMobile && "w-full")}
            >
              {t("common.cancel")}
            </Button>
          </div>
        </form>
      </div>
    </div>
    </>
  );
}
